import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Compass, Handshake, TrendingUp, FileText, ArrowRight, Download } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSite } from '@/context/SiteContext';

const STEPS = [
    { icon: Compass, title: 'Discover', body: 'We understand your CSR priorities and geography.' },
    { icon: Handshake, title: 'Align', body: 'Match VVVSI programs with your outcomes framework.' },
    { icon: TrendingUp, title: 'Execute', body: 'On-ground delivery via our 3,400+ volunteer network.' },
    { icon: FileText, title: 'Report', body: 'Transparent quarterly impact reports and audits.' },
];

const CSRPartnershipSection = () => {
    const { settings } = useSite();
    return (
        <section
            className="section-pad bg-[hsl(var(--primary))]/[0.04]"
            data-testid="csr-partnership-section"
        >
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="accent-bar" />
                            <span className="text-[hsl(var(--accent))] font-semibold text-xs uppercase tracking-widest">
                                CSR Partnership
                            </span>
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
                            Partner with us to deliver measurable social impact.
                        </h2>
                        <p className="mt-4 text-muted-foreground leading-relaxed">
                            VVVSI works with corporates and institutions to design and execute
                            outcome-driven CSR programs — with rigorous reporting and on-ground
                            transparency.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            {settings?.brochure_url && (
                                <a
                                    href={settings.brochure_url}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    data-testid="csr-brochure-download"
                                >
                                    <Button className="bg-[hsl(var(--primary))] text-primary-foreground hover:bg-[hsl(var(--primary))]/90">
                                        <Download className="h-4 w-4 mr-1.5" /> Download CSR Deck
                                    </Button>
                                </a>
                            )}
                            <a href="/contact">
                                <Button variant="outline">
                                    Talk to CSR Team <ArrowRight className="h-4 w-4 ml-1.5" />
                                </Button>
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-2 gap-4">
                            {STEPS.map((s, i) => {
                                const Icon = s.icon;
                                return (
                                    <motion.div
                                        key={s.title}
                                        initial={{ opacity: 0, y: 12 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4, delay: i * 0.05 }}
                                        viewport={{ once: true, margin: '-60px' }}
                                    >
                                        <Card className="h-full bg-card border-border shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
                                            <CardContent className="p-6">
                                                <div className="text-xs font-semibold text-[hsl(var(--accent))] tabular-nums">
                                                    STEP 0{i + 1}
                                                </div>
                                                <div className="mt-2 flex items-center gap-2">
                                                    <Icon className="h-5 w-5 text-[hsl(var(--primary))]" />
                                                    <div className="font-display font-semibold text-lg">
                                                        {s.title}
                                                    </div>
                                                </div>
                                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                                    {s.body}
                                                </p>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CSRPartnershipSection;
